
import FuelStopDetailView from '@components/fuel-stop/fuel-stop-detail/FuelStopDetailView.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@util/types';
import { FuelStop, FuelStopMutation, Pilot, Trip } from '@clients/myTrips';
import { RouterFrom } from '@router/Routes';
import goBackWithFallback from '@util/goBackWithFallback';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import PositionMap from '@components/my-trips/PositionMap.vue';
import { Location as RouterLocation } from 'vue-router'
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import deepCopy from '@util/copy';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: "FuelStopEdit",
  components: {
    AppHeader,
    InfoBoxWarning,
    AppPage,
    AppContent,
    FuelStopDetailView,
    SettingsFooter,
    PositionMap,
    SettingsDelete
  }
})
export default class FuelStopEdit extends Vue {
  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getPilots')
  private pilots: Pilot[];

  @TriplogModule.Action('fetchFuelStop')
  private fetchFuelStop;

  @TriplogModule.Getter('getFuelStopStatus')
  private fuelStopStatus: LoadState

  @TriplogModule.Getter('getFuelStop')
  private fuelStop: FuelStop

  @TriplogModule.Action('updateFuelStop')
  private updateFuelStop;

  @TriplogModule.Getter('updateFuelStopStatus')
  private fuelStopUpdateStatus: LoadState;

  @TriplogModule.Action('deleteFuelStop')
  private deleteFuelStop;

  @TriplogModule.Getter('deleteFuelStopStatus')
  private fuelStopDeleteStatus: LoadState;

  @TriplogModule.Action('fetchOldestTrip')
  private fetchOldestTrip;

  @RouterModule.State('from')
  private from: RouterFrom;

  private disabled = false;
  private isFetching = true;

  private oldestTrip: Trip = null;

  private deleteText = 'Sind Sie sicher, dass Sie die Betankung löschen möchten?';

  private errorMessage = '';

  private fuelStopEdit: FuelStopMutation = null;

  async created() {
    this.fuelStopEdit = { ...this.fuelStop }

    if (!this.queryVehicleId) {
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    }
    this.fetchPilots();

    const [fuelStop, oldestTrip] = await Promise.all([this.fetchFuelStop({ vehicleId: this.queryVehicleId, id: this.paramsFuelStopId }), this.fetchOldestTrip({ vehicleId: this.queryVehicleId })]);
    this.fuelStopEdit = {
      ...deepCopy(fuelStop),
      pilotId: this.fuelStop.pilot?.id || null
    };
    this.oldestTrip = oldestTrip

    this.isFetching = false;
  }

  get backButton() {
    return { text: 'Zurück zu Fahrten', location: this.backLocation };
  }

  get backLocation(): RouterLocation {
    return { name: 'myTrips', query: { vehicle: this.queryVehicleId.toString() } };
  }

  get minTimestamp(): string {
    if (!this.oldestTrip) {
      return null;
    }

    const fuelStopBeforeOldestTrip = this.oldestTrip.fuelStops
        .filter(fs => fs.timestamp <= this.oldestTrip.startInfo.timestamp);
    if (fuelStopBeforeOldestTrip.length === 0 || fuelStopBeforeOldestTrip[0].id === this.paramsFuelStopId) {
      return null;
    }

    const minTime = new Date(this.oldestTrip.startInfo.timestamp);
    minTime.setMinutes(minTime.getMinutes() + 1);
    return minTime.toISOString();
  }

  get isLoading(): boolean {
    return this.pilotsStatus === LoadState.Loading
        || this.fuelStopStatus === LoadState.Loading
        || this.isFetching;
  }

  get hasLoadError(): boolean {
    return this.pilotsStatus === LoadState.Error
        || this.fuelStopStatus === LoadState.Error;
  }

  get hasInputError(): boolean {
    return !this.fuelStopEdit.address
        || !this.fuelStopEdit.location
        || !this.fuelStopEdit.timestamp
        || !this.fuelStopEdit.litres
        || this.fuelStopEdit.litres <= 0
        || !this.fuelStopEdit.totalPrice
        || new Date(this.fuelStopEdit.timestamp) <= new Date(this.minTimestamp)
        || new Date(this.fuelStopEdit.timestamp) > new Date();
  }

  get queryVehicleId(): number {
    return parseInt(this.$route.query.vehicle as string);
  }

  get paramsFuelStopId(): number {
    return parseInt(this.$route.params.id as string);
  }

  private deleteFuelStopButton() {
    if (window.confirm(this.deleteText)) {
      this.handleFuelStopDelete();
    }
  }

  private async handleFuelStopSubmit() {
    if(!this.hasInputError) {
      this.disabled = true;
      try {
        await this.updateFuelStop({vehicleId: this.fuelStop.vehicleId, id: this.fuelStop.id, fuelStopMutation: this.fuelStopEdit})
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        this.errorMessage = 'Fehler beim Speichern der Betankung. Bitte versuchen sie es später erneut.'
      } finally {
        this.disabled = false;
      }
    }
  }

  private async handleFuelStopDelete() {
    try {
      await this.deleteFuelStop({ vehicleId: this.fuelStop.vehicleId, id: this.fuelStop.id, fuelStop: this.fuelStop });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } catch (error) {
      this.errorMessage = 'Fehler beim Löschen der Betankung. Bitte versuchen sie es später erneut.'
    }
  }
}
